/* eslint-disable prettier/prettier */
/* prettier-ignore-file */

import { apiService } from './api.service';

export interface ActivityType {
	id: string;
	title: string;
	color: string;
	icon: string | null;
}

export interface CoachInfo {
	id: string;
	fullname: string;
	email: string;
	profilepicture: string | null;
	designation: string | null;
}

export interface UpcomingLesson {
	lessonid: string;
	moduleid: string;
	courseid: string;
	enrollmentid: string;

	activitytypeid: string | null;

	lessonname: string;
	modulename: string;
	programname: string;

	sessiontype: string;
	contenttype: string;

	scheduledstarttime: string;
	scheduledendtime: string;

	timezone: string;
	duration: number | null;

	meetingurl: string | null;
	recordingurl: string | null;

	attendancerequired: boolean;

	activitytype: ActivityType | null;

	coach: CoachInfo | null;
}

export interface LearnerUpcomingLessons {
	learnerid: string;
	upcoming: UpcomingLesson[];
}

/* =========================
   CREATE MENTORING LESSON
========================= */

export interface CreateMentoringLessonPayload {
	moduleid: string;
	learnerId: string;
	title: string;
	description?: string;
	scheduledstart: string;
	scheduledend: string;
	meetingurl?: string;
	activitytypeid?: string;
	orderindex: number;
	duration?: number;
}

export interface MentoringLesson {
	id: string;
	moduleid: string;
	title: string;
	description: string | null;
	orderindex: number;
	contenttype: string;
	duration: number | null;
	activitytypeid: string | null;
	assignedto: string;
	deliveredby: string;
}

export interface MentoringSession {
	id: string;
	hostid: string;
	title: string;
	description: string | null;
	sessiontype: string;
	scheduledstart: string;
	scheduledend: string;
	meetingurl: string | null;
	status: string;
	maxparticipants: number;
	lessonid: string;
}

export interface CreateMentoringLessonResponse {
	lesson: MentoringLesson;
	session: MentoringSession;
}

/* =========================
   ATTENDANCE
========================= */

export type AttendanceStatus = 'attended' | 'no-show';

export interface MarkAttendanceSinglePayload {
	status: AttendanceStatus;
}

export interface MarkAttendanceBulkItem {
	userid: string;
	status: AttendanceStatus;
}

export interface MarkAttendanceBulkPayload {
	attendees: MarkAttendanceBulkItem[];
}

export interface MarkAttendanceBulkResponse {
	updated: number;
	failed: string[];
}

export const coachingService = {
	async getLearnerUpcomingLessons(
		learnerId: string,
		limit: number = 2
	): Promise<LearnerUpcomingLessons> {
		try {
			const response = await apiService.get<LearnerUpcomingLessons>(
				`/course/learner/${learnerId}/upcoming?limit=${limit}`
			);

			if (!response.success) {
				throw new Error(response.message);
			}

			return response.data;
		} catch (error: any) {
			console.error('Upcoming lessons fetch error:', error);

			throw new Error(
				error.message || 'Failed to fetch upcoming lessons'
			);
		}
	},

	async createMentoringLesson(
		payload: CreateMentoringLessonPayload
	): Promise<CreateMentoringLessonResponse> {
		try {
			const response =
				await apiService.post<CreateMentoringLessonResponse>(
					'/coaching/mentoring-lesson',
					payload
				);

			if (!response.success) {
				throw new Error(response.message);
			}

			return response.data;
		} catch (error: any) {
			console.error('Create mentoring lesson error:', error);

			throw error;
		}
	},

	/* =========================
   MARK SINGLE ATTENDANCE
========================= */

	async markAttendanceSingle(
		sessionId: string,
		userId: string,
		payload: MarkAttendanceSinglePayload
	): Promise<void> {
		try {
			const response = await apiService.patch(
				`/coaching/${sessionId}/attendance/${userId}`,
				payload
			);

			if (!response.success) {
				throw new Error(response.message);
			}
		} catch (error: any) {
			console.error('Mark single attendance error:', error);

			throw new Error(error.message || 'Failed to mark attendance');
		}
	},

	/* =========================
   MARK BULK ATTENDANCE
========================= */

	async markAttendanceBulk(
		sessionId: string,
		payload: MarkAttendanceBulkPayload
	): Promise<MarkAttendanceBulkResponse> {
		try {
			const response = await apiService.patch<MarkAttendanceBulkResponse>(
				`/coaching/sessions/${sessionId}/attendance`,
				payload
			);

			if (!response.success) {
				throw new Error(response.message);
			}

			return response.data;
		} catch (error: any) {
			console.error('Mark bulk attendance error:', error);

			throw new Error(error.message || 'Failed to mark bulk attendance');
		}
	},
};
